/**
 * Production-Ready API Configuration
 * 
 * This configuration provides production-ready settings for API handling,
 * including retry policies, timeout configurations, and error handling.
 */

import { toast } from 'react-toastify';

/**
 * Environment-based configuration
 */
const ENV_CONFIG = {
  development: {
    timeout: 30000,
    retries: 2,
    retryDelay: 1000,
    enableLogging: true,
    enableDevtools: true
  },
  staging: {
    timeout: 25000,
    retries: 3,
    retryDelay: 1500,
    enableLogging: true,
    enableDevtools: false
  },
  production: {
    timeout: 20000,
    retries: 3,
    retryDelay: 2000,
    enableLogging: false,
    enableDevtools: false
  }
};

/**
 * Get current environment configuration
 */
const getCurrentConfig = () => {
  const env = process.env.NODE_ENV || 'development';
  return ENV_CONFIG[env] || ENV_CONFIG.development;
};

/**
 * API Endpoints Configuration
 */
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/auth/login',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    REGISTER: '/auth/register',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_2FA: '/auth/verify-2fa'
  },
  
  // User Management
  USER: {
    PROFILE: '/user/profile',
    UPDATE_PROFILE: '/user/profile',
    ACCESS_STATUS: '/user/access_status',
    UPGRADE_LEVEL_ONE: '/user/upgradeLevelOne',
    UPGRADE_LEVEL_TWO: '/user/upgradeLevelTwo',
    REFERRALS: '/user/referrals'
  },
  
  // Wallet Operations
  WALLET: {
    LIST: '/user_wallets/list',
    DEPOSIT_ID: '/user_wallets/get_deposit_id',
    INTERNAL_TRANSFER: '/user_wallets/internal_transfer',
    GIFT_CODE: '/user_wallets/gift_code_charge'
  },
  
  // Trading
  TRADING: {
    MARKETS: '/markets',
    ORDERS: '/orders',
    ORDER_HISTORY: '/order/history',
    CANCEL_ORDER: '/order/cancel',
    QUICK_ORDER: '/order/quick'
  },
  
  // Transactions
  TRANSACTIONS: {
    LIST: '/transactions',
    SHETAB: '/getTransactions',
    DEPOSIT: '/deposit',
    WITHDRAW: '/withdraw'
  },
  
  // Coins and Markets
  COINS: {
    LIST: '/coin_list',
    OTC_LIST: '/otc_coins',
    NETWORK: '/network_any_currency',
    PRICES: '/prices'
  },
  
  // Support and Notifications
  SUPPORT: {
    TICKETS: '/support/tickets',
    CREATE_TICKET: '/support/create',
    NOTIFICATIONS: '/notifications'
  }
};

/**
 * Request Configuration Templates
 */
export const REQUEST_CONFIGS = {
  // Standard GET request
  GET: {
    method: 'GET',
    timeout: getCurrentConfig().timeout,
    retry: getCurrentConfig().retries
  },
  
  // POST request for data creation
  POST_CREATE: {
    method: 'POST',
    timeout: getCurrentConfig().timeout,
    retry: 1, // Don't retry creation requests to avoid duplicates
    headers: {
      'Content-Type': 'application/json'
    }
  },
  
  // POST request for actions (can be retried)
  POST_ACTION: {
    method: 'POST',
    timeout: getCurrentConfig().timeout,
    retry: getCurrentConfig().retries,
    headers: {
      'Content-Type': 'application/json'
    }
  },
  
  // File upload request
  UPLOAD: {
    method: 'POST',
    timeout: 60000, // Longer timeout for file uploads
    retry: 1,
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  },
  
  // Real-time data request (shorter timeout)
  REALTIME: {
    method: 'GET',
    timeout: 10000,
    retry: 1
  }
};

/**
 * Error Handling Configuration
 */
export const ERROR_CONFIG = {
  // HTTP status codes that should trigger retries
  RETRYABLE_STATUS_CODES: [408, 429, 500, 502, 503, 504],
  
  // HTTP status codes that should NOT trigger retries
  NON_RETRYABLE_STATUS_CODES: [400, 401, 403, 404, 422],
  
  // Error messages for different scenarios
  MESSAGES: {
    NETWORK_ERROR: 'Network error. Please check your connection.',
    TIMEOUT_ERROR: 'Request timed out. Please try again.',
    SERVER_ERROR: 'Server error. Please try again later.',
    VALIDATION_ERROR: 'Please check your input and try again.',
    UNAUTHORIZED: 'Session expired. Please log in again.',
    FORBIDDEN: 'You do not have permission to perform this action.',
    NOT_FOUND: 'The requested resource was not found.',
    RATE_LIMITED: 'Too many requests. Please wait and try again.',
    GENERIC_ERROR: 'An unexpected error occurred. Please try again.'
  }
};

/**
 * Cache Configuration
 */
export const CACHE_CONFIG = {
  // Default cache times for different types of data
  CACHE_TIMES: {
    USER_DATA: 1000 * 60 * 15, // 15 minutes
    WALLET_DATA: 1000 * 60 * 5, // 5 minutes
    MARKET_DATA: 1000 * 60 * 2, // 2 minutes
    STATIC_DATA: 1000 * 60 * 60, // 1 hour
    REALTIME_DATA: 1000 * 30 // 30 seconds
  },
  
  // Stale times (when data is considered stale but still usable)
  STALE_TIMES: {
    USER_DATA: 1000 * 60 * 5, // 5 minutes
    WALLET_DATA: 1000 * 60 * 2, // 2 minutes
    MARKET_DATA: 1000 * 30, // 30 seconds
    STATIC_DATA: 1000 * 60 * 30, // 30 minutes
    REALTIME_DATA: 1000 * 10 // 10 seconds
  }
};

/**
 * Rate Limiting Configuration
 */
export const RATE_LIMIT_CONFIG = {
  // Maximum requests per time window
  MAX_REQUESTS: {
    GENERAL: 100, // per minute
    AUTH: 5, // per minute
    TRADING: 50, // per minute
    REALTIME: 200 // per minute
  },
  
  // Time windows in milliseconds
  TIME_WINDOWS: {
    GENERAL: 60000, // 1 minute
    AUTH: 60000, // 1 minute
    TRADING: 60000, // 1 minute
    REALTIME: 60000 // 1 minute
  }
};

/**
 * Performance Monitoring Configuration
 */
export const PERFORMANCE_CONFIG = {
  // Enable performance monitoring
  ENABLED: getCurrentConfig().enableLogging,
  
  // Thresholds for performance warnings
  THRESHOLDS: {
    SLOW_REQUEST: 3000, // 3 seconds
    VERY_SLOW_REQUEST: 10000, // 10 seconds
    HIGH_ERROR_RATE: 0.1 // 10% error rate
  },
  
  // Metrics to track
  METRICS: {
    REQUEST_DURATION: true,
    ERROR_RATE: true,
    CACHE_HIT_RATE: true,
    RETRY_COUNT: true
  }
};

export default {
  ENV_CONFIG,
  getCurrentConfig,
  API_ENDPOINTS,
  REQUEST_CONFIGS,
  ERROR_CONFIG,
  CACHE_CONFIG,
  RATE_LIMIT_CONFIG,
  PERFORMANCE_CONFIG
};
